/* eslint-disable jsdoc/check-param-names */
import { BrickContext } from 'olympe';
import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete } from '@mui/material';
import debounce from 'lodash.debounce';
import { useProperty } from '@olympeio/core';
import Chip from '@mui/material/Chip';
import { PopupSubtitle } from './BasePopup.jsx';
import { SearchTextfield } from './SearchTextfield.jsx';
import { advancedSearchFormats, AdvancedSearchKey } from './_enums';

/**
 * Search input that allow selection of multiple values. Three different data are supported
 * - Cost Centers: displayed as chips
 * - WBS/PRN: displayed as chips
 * - Approvers: displayed as a vertical list of avatars + name label
 * @param {BrickContext} context
 * @param {Array} data
 * @param {AdvancedSearchKey} searchKey
 * @param {string} title
 * @param {string} placeholderText
 * @returns {React.ReactElement}
 * @class
 */
// prettier-ignore
export const AdvancedSearchInput = ({ context, data, searchKey, title = searchKey, placeholderText = 'Search By ID Or Name'}) => {

	const [text, setText] = useState('');
	const [value, setValue] = useState(null);
	const [options, setOptions] = useState([]);
	const selectedContextKey = `Selected ${searchKey}`;
	const selectedOptions = useProperty(context, selectedContextKey, false) || [];
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [pasteQuery, setPasteQuery] = useState(false);

	const querySearchValues = (text) => {
		context.set(searchKey + ' Filter Text', text);
		context.trigger('Fetch ' + searchKey);

	}
	// debounce query and keep same callback between react renders (see https://dmitripavlutin.com/react-throttle-debounce/)
	const debouncedQuery = useMemo(() => debounce(querySearchValues, 300), []);

	const onChangeHandler = (e, textValue, reason) => {
		if(reason === 'reset') {
			setText(''); // if a value has been selected, clear the textfield
		} else {
			setText(textValue);
		}
		if (reason === 'input' && (
			searchKey === AdvancedSearchKey.APPROVER && textValue.length > 1 || // allow 2 characters when searching approvers (for example PL*)
			searchKey === AdvancedSearchKey.AGENT && textValue.length > 1 || // allow 2 characters when searching agents (for example PL*)
			searchKey === AdvancedSearchKey.PURCHASE_GROUP && textValue.length > 1 || // allow 2 characters when searching approvers (for example PL*)
			textValue.length > 3
		)) {
			setLoading(true);
			setOpen(true);
			debouncedQuery(textValue);
		}
	}

	const pushNewSelectedValue = (newValue) => {
		const key = getOptionLabel(newValue);
		if (selectedOptions.find((opt) => getOptionLabel(opt) === key) === undefined) {
			context.set(selectedContextKey, [...selectedOptions, newValue]);
		}
	}

	const onSelectHandler = (event, newValue) => {
		setValue(newValue);
		setOpen(false);
		pushNewSelectedValue(newValue);
	}

	const onRemoveFromList = (idx, e) => {
		context.set(selectedContextKey, selectedOptions.filter((item, index) => index !== idx));
	}

	// finish loading state once data is received/updated
	useEffect(() => {
		setLoading(false);
		if (data && Array.isArray(data) && data.length > 0) {
			// if pasted and only one exact match is found, apply immediately
			if(pasteQuery && data.length === 1 &&
				(data[0].ApproverName === text || data[0].CostCenterId === text || data[0].PrnNo === text)
			) {
				pushNewSelectedValue(data[0]);
				setText('');
				setOptions([]);
				setOpen(false); // close popup when successfully pasting
			} else {
				setOptions(data);
			}
			setPasteQuery(false);
		} else {
			setOptions([]);
		}
	}, [data])

	// reset selection and available values on external change
	context.observe(`Clear ${searchKey}`, true).subscribe((value) => {
		if(value !== null) {
			context.remove(`Clear ${searchKey}`); // prevent loops upon re-rendering
			context.set(selectedContextKey, []);
			context.set(searchKey, []);
			setText('');
			setValue(null);
		}
	});

	let getOptionLabel;
	// label consisting of the object ID and name, ID must be included so that each entry has an unique key in the Dropdown list
	if(searchKey === AdvancedSearchKey.APPROVER) {
		getOptionLabel = (item) => `${item.ApproverId} - ${item.ApproverName}`;
	} else if(searchKey === AdvancedSearchKey.AGENT) {
		getOptionLabel= (item) => `${item.PosIdName} (${item.PosId})`
	} else if(searchKey === AdvancedSearchKey.COST_CENTER) {
		getOptionLabel = (item) => `${item.CostCenterId} - ${item.CostCenterDescription}`;
	} else if(searchKey === AdvancedSearchKey.PURCHASE_GROUP) {
		getOptionLabel = (item) => `${item.PurchGroup} - ${item.PurchGroupName}`;	
	} else if(searchKey === AdvancedSearchKey.PRN_BY_WBS_ELEMENT) {
		getOptionLabel = (item) => `${item.WbsElement} (PRN ${item.PrnNo})`;
	} else {
		// cases PrnByInternal and PrnByNumber
		getOptionLabel =  (item) => item.PrnNo + (item.PrnName ? `- ${item.PrnName}` : '');
	}

	const exampleFormat = advancedSearchFormats[searchKey] || '';

	return (
		<div className={'advanced-search-input-container'} style={{ marginBottom: '4vh' }}>
			<PopupSubtitle
				subtitle={title}
			/>
			<Autocomplete
				className="popup-input"
				value={value}
				inputValue={text}
				onChange={onSelectHandler}
				open={open}
				loading={loading}
				onInputChange={onChangeHandler}
				onPaste={() => {
					context.set(searchKey, []); // force reset "data" variable in case of pasting, deleting the value, and pasting again
					setPasteQuery(true); // query will trigger through onInputChange
				}}
				onBlur={() => {
					setOpen(false);
				}}
				options={options}
				getOptionLabel={(item) => {
					if (item) {
						return getOptionLabel(item);
					} else {
						return ""
					}
				}}
				disableClearable={true}
				size={'small'}
				renderInput={(params) =>
					<SearchTextfield params={params} value={text} placeholder={placeholderText} exampleFormat={exampleFormat}/>
				}
			/>
			<div
				className={'advanced-search-chip-array'}
				style={{marginTop: '1vh', height: 'fit-content'}}
			>
				{selectedOptions.map((item, idx) =>
					<Chip
						key={`chip-${searchKey}-${idx}`}
						className='advanced-search-chip'
						label={getOptionLabel(item)}
						color='primary'
						variant='outlined'
						onDelete={(e) => onRemoveFromList(idx, e)}
					/>
				)}
			</div>
		</div>
	);
}
